import { useEffect, useMemo, useRef, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import mailroomService from "api/mailroomService";
import radixService from "api/radixService";
import { EParcelStoreKind } from "dto/Mailroom/IMailroom";
import { ERadixAddressType, ERadixProgram, IRadixImportProgress } from "dto/Radix/IRadix";
import { ENotificationType, notification } from "utils/notification";
import { radixErrorText } from "./radixUtils";

import styles from "./radix.module.scss";

interface Props {
  program: ERadixProgram;
  /** Miejscowości lokalne z ustawień RADIX (domyślnie wszystkie zaznaczone). */
  places: string[];
  onClose: () => void;
  onImported: () => void;
}

// Kreator importu — port ImportModal.xaml / ImportViewModel z Kwantum.Radix.UI:
// plik RTF (wydruk z RADIX) → nazwa paczki (domyślnie nazwa pliku) → adresowanie
// (karta podatnika / adres nieruchomości) → dublowanie decyzji współwłaścicieli →
// rodzaj przesyłki (słownik form doręczenia) → ustawienia strony → miejscowości lokalne.
// Import biegnie w tle na BE; modal odpytuje postęp co sekundę (jak progressTimer).

const TEMPLATE_MARK: Record<ERadixProgram, string> = {
  [ERadixProgram.WIP]: "{Id: <NR_AKTDOSO>, Nr: <NRUPO>}",
  [ERadixProgram.PoGrun]: "{Id: <ID_ADRESATA>, Nr: <NR_REJESTR>}",
  [ERadixProgram.GOK]: "{Id: <WLA_ID>}",
};

const TITLE: Record<ERadixProgram, string> = {
  [ERadixProgram.WIP]: "Import upomnień z programu RADIX WIP",
  [ERadixProgram.PoGrun]: "Import decyzji z programu RADIX PoGrun",
  [ERadixProgram.GOK]: "Import zawiadomień z programu RADIX GOK",
};

const ImportRadixModal = ({ program, places, onClose, onImported }: Props) => {
  const { data: kindsData } = mailroomService.useGetDeliveryKinds();
  const { data: storesData } = mailroomService.useGetParcelStores();
  const { mutate: startImport, isLoading: starting } = radixService.useStartImport();

  const fileInput = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [groupName, setGroupName] = useState("");
  const [addressType, setAddressType] = useState<ERadixAddressType>(ERadixAddressType.AddressOfResidence);
  const [ignoreDuplicates, setIgnoreDuplicates] = useState(true);
  const [deliveryKindId, setDeliveryKindId] = useState<number | null>(null);
  const [storeId, setStoreId] = useState<string>("");
  const [generatePdf, setGeneratePdf] = useState(true);
  const [pageOpen, setPageOpen] = useState(false);
  const [overridePage, setOverridePage] = useState(true);
  const [margins, setMargins] = useState({ left: 15, right: 10, top: 10, bottom: 10, header: 12, footer: 12 });
  const [checkedPlaces, setCheckedPlaces] = useState<Set<string>>(new Set(places));
  const [progress, setProgress] = useState<IRadixImportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Papierowe formy doręczenia (magistracki EnumComboBox ParcelType, domyślnie PUH → tu list polecony).
  const kinds = useMemo(
    () =>
      (kindsData?.data ?? [])
        .filter((k) => !k.IsElectronic && k.Name !== "Inne")
        .slice()
        .sort((a, b) => a.SortOrder - b.SortOrder),
    [kindsData],
  );

  useEffect(() => {
    if (deliveryKindId === null && kinds.length > 0) {
      const registered = kinds.find((k) => k.Name === "List polecony") ?? kinds[0];
      setDeliveryKindId(registered.Id);
    }
  }, [kinds, deliveryKindId]);

  const stores = useMemo(
    () =>
      (storesData?.data ?? []).filter(
        (s) => s.Kind === EParcelStoreKind.OutgoingParcel && s.IsActive && !s.IsElectronic,
      ),
    [storesData],
  );

  // Odpytywanie postępu importu w tle (co sekundę), aż do zakończenia.
  useEffect(() => {
    if (!progress || progress.Finished) return;
    const t = window.setInterval(async () => {
      try {
        const { data } = await radixService.getImportProgress();
        setProgress(data);
        if (data.Finished) {
          window.clearInterval(t);
          if (data.Success) {
            notification({ type: ENotificationType.SUCCESS, title: "Import RADIX", text: data.ResultMessage ?? "Zaimportowano." });
            onImported();
          }
        }
      } catch {
        /* chwilowy błąd sieci — kolejny tick */
      }
    }, 1000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress?.JobId, progress?.Finished]);

  const pickFile = (f: File | null) => {
    setFile(f);
    if (f) setGroupName(f.name.replace(/\.[^.]+$/, ""));
  };

  const togglePlace = (p: string) =>
    setCheckedPlaces((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });

  const canSubmit = !!file && groupName.trim().length > 0 && deliveryKindId !== null && !starting && !(progress && !progress.Finished);

  const submit = () => {
    if (!file || deliveryKindId === null) return;
    setError(null);
    startImport(
      {
        file,
        request: {
          Program: program,
          GroupName: groupName.trim(),
          ParcelStoreId: storeId || null,
          DeliveryKindId: deliveryKindId,
          AddressType: addressType,
          IgnoreDuplicatedDecisionsForSameAddress: ignoreDuplicates,
          OverridePageSettings: overridePage,
          LeftMargin: margins.left,
          RightMargin: margins.right,
          TopMargin: margins.top,
          BottomMargin: margins.bottom,
          PageHeader: margins.header,
          PageFooter: margins.footer,
          Places: [...checkedPlaces],
          GeneratePdf: generatePdf,
        },
      },
      {
        onSuccess: (res) =>
          setProgress({ JobId: res.data.JobId, Active: true, Finished: false, Success: false, Message: "Rozpoczynanie importu", Current: 0, Total: 0 }),
        onError: (e) => setError(radixErrorText(e, "Nie udało się uruchomić importu.")),
      },
    );
  };

  const running = !!progress && !progress.Finished;
  const percent = progress && progress.Total > 0 ? Math.round((progress.Current / progress.Total) * 100) : 0;

  return (
    <div className={styles.overlay} onMouseDown={running ? undefined : onClose}>
      <div className={`${styles.modal} ${styles.modalWide}`} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.modalTitle}>{TITLE[program]}</div>
        <div className={styles.modalBody}>
          {progress ? (
            <>
              <div className={styles.progressText}>
                {progress.Finished ? progress.ResultMessage : progress.Message}
                {!progress.Finished && progress.Total > 0 ? ` ${progress.Current}/${progress.Total}` : ""}
              </div>
              {!progress.Finished && (
                <div className={styles.progressBar}>
                  <div className={styles.progressFill} style={{ width: `${percent}%` }} />
                </div>
              )}
              {progress.Finished && !progress.Success && <div className={styles.error}>Import nie powiódł się.</div>}
            </>
          ) : (
            <div className={styles.twoCols}>
              <div className={styles.col}>
                <div className={styles.hint}>
                  Aby poprawnie wczytać dokumenty z programu RADIX, w szablonie dokumentu dla grupy pojedynczego
                  podatnika dodaj na końcu znacznik: <span className={styles.mark}>{TEMPLATE_MARK[program]}</span>.
                  Dostarczony plik RTF zostanie podzielony na pojedyncze dokumenty w miejscach wystąpień znacznika.
                </div>

                <div className={styles.field}>
                  <label>Wydruk dokumentów z programu RADIX</label>
                  <div className={styles.fileRow}>
                    <input type="text" readOnly value={file?.name ?? ""} placeholder="Wybierz plik RTF" onClick={() => fileInput.current?.click()} />
                    <HollowButton text="Przeglądaj" onClick={() => fileInput.current?.click()} />
                    <input
                      ref={fileInput}
                      type="file"
                      accept=".rtf,application/rtf,text/rtf"
                      style={{ display: "none" }}
                      onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                </div>

                <div className={styles.field}>
                  <label>Nazwa</label>
                  <input
                    type="text"
                    value={groupName}
                    placeholder="Wprowadź nazwę wspólną dla zaimportowanych dokumentów"
                    onChange={(e) => setGroupName(e.target.value)}
                  />
                </div>

                <div className={styles.field}>
                  <label>Adresowanie</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.check}>
                      <input type="radio" checked={addressType === ERadixAddressType.AddressOfResidence} onChange={() => setAddressType(ERadixAddressType.AddressOfResidence)} />
                      Z karty podatnika
                    </label>
                    <label className={styles.check}>
                      <input type="radio" checked={addressType === ERadixAddressType.RealEstateAddress} onChange={() => setAddressType(ERadixAddressType.RealEstateAddress)} />
                      Z adresu nieruchomości
                    </label>
                  </div>
                </div>

                <div className={styles.field}>
                  <label>Dublowanie decyzji dla współwłaścicieli mieszkających pod tym samym adresem</label>
                  <div className={styles.radioGroup}>
                    <label className={styles.check}>
                      <input type="radio" checked={ignoreDuplicates} onChange={() => setIgnoreDuplicates(true)} />
                      Nie dubluj
                    </label>
                    <label className={styles.check}>
                      <input type="radio" checked={!ignoreDuplicates} onChange={() => setIgnoreDuplicates(false)} />
                      Dubluj
                    </label>
                  </div>
                </div>

                <div className={styles.grid2}>
                  <div className={styles.field}>
                    <label>Rodzaj przesyłki</label>
                    <select value={deliveryKindId ?? ""} onChange={(e) => setDeliveryKindId(Number(e.target.value))}>
                      {kinds.map((k) => (
                        <option key={k.Id} value={k.Id}>
                          {k.Name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.field}>
                    <label>Skład wychodzący (numeracja)</label>
                    <select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                      <option value="">domyślny papierowy</option>
                      {stores.map((s) => (
                        <option key={s.Id} value={s.Id}>
                          {s.Name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <label className={styles.check}>
                  <input type="checkbox" checked={generatePdf} onChange={(e) => setGeneratePdf(e.target.checked)} />
                  Generuj PDF każdego pisma i zbiorczy PDF paczki do druku
                </label>

                <div className={styles.collapsible}>
                  <div className={styles.collapsibleHead} onClick={() => setPageOpen((v) => !v)}>
                    {pageOpen ? "▾" : "▸"} Ustawienia strony
                  </div>
                  {pageOpen && (
                    <div className={styles.col} style={{ marginTop: 8 }}>
                      <label className={styles.check}>
                        <input type="checkbox" checked={overridePage} onChange={(e) => setOverridePage(e.target.checked)} />
                        Użyj poniższych ustawień strony
                      </label>
                      <div className={styles.grid2}>
                        {(
                          [
                            ["left", "Lewy margines [mm]"],
                            ["right", "Prawy margines [mm]"],
                            ["top", "Górny margines [mm]"],
                            ["bottom", "Dolny margines [mm]"],
                            ["header", "Nagłówek [mm]"],
                            ["footer", "Stopka [mm]"],
                          ] as const
                        ).map(([k, label]) => (
                          <div key={k} className={styles.field}>
                            <label>{label}</label>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              disabled={!overridePage}
                              value={margins[k]}
                              onChange={(e) => setMargins({ ...margins, [k]: Math.max(0, Number(e.target.value) || 0) })}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {error && <div className={styles.error}>{error}</div>}
              </div>

              <div className={styles.col}>
                <div className={styles.field}>
                  <label>Miejscowości (doręczenie osobiste)</label>
                  {places.length === 0 ? (
                    <div className={styles.hint}>Brak miejscowości lokalnych — dodaj je w ustawieniach RADIX.</div>
                  ) : (
                    <div className={styles.placesList}>
                      {places.map((p) => (
                        <label key={p} className={styles.check}>
                          <input type="checkbox" checked={checkedPlaces.has(p)} onChange={() => togglePlace(p)} />
                          {p}
                        </label>
                      ))}
                    </div>
                  )}
                  <div className={styles.hint}>
                    Adresaci z zaznaczonych miejscowości dostają formę „Doręczenie osobiste" zamiast wybranego rodzaju przesyłki.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          {progress?.Finished ? (
            <Button text="Zamknij" onClick={onImported} />
          ) : (
            <>
              <HollowButton text="Anuluj" onClick={onClose} disabled={running} />
              <Button text="Importuj" onClick={submit} loading={starting || running} disabled={!canSubmit} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportRadixModal;
